import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { admin, ApiError, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { date } from "../lib/format";
import type { Domain, List, ThemeDoc, ThemeLink, ThemePreset, ThemeSection, ThemeState, ThemeVersion, Upload } from "../lib/types";
import { Alert, Badge, Button, Field, Spinner } from "../components/ui";
import { thumb } from "../components/ImageManager";

// Editorul temei (docs/research/design-customizare.md §3): modificările merg într-o ciornă, salvată automat și
// afișată în previzualizarea din dreapta (magazinul real, randat cu ciorna); „Publică” o pune în magazin.

type Tab = "presets" | "style" | "sections" | "nav" | "history";
const TABS: [Tab, string][] = [["presets", "Teme"], ["style", "Stil"], ["sections", "Secțiuni"], ["nav", "Navigare"], ["history", "Istoric"]];

// Perechile de fonturi (aceleași ca theme.FontPairs); mostrele se văd în fonturile reale, încărcate în admin.
type FontOpt = { id: string; label: string; h: string; b: string; w: number };
const FONTS: FontOpt[] = [
  { id: "echilibrat", label: "Instrument Sans", h: "Instrument Sans Variable", b: "Instrument Sans Variable", w: 600 },
  { id: "vitrina", label: "Bodoni Moda + Jost", h: "Bodoni Moda Variable", b: "Jost Variable", w: 500 },
  { id: "atelier", label: "Alegreya + Alegreya Sans", h: "Alegreya Variable", b: "Alegreya Sans", w: 700 },
  { id: "catalog", label: "Archivo", h: "Archivo Variable", b: "Archivo Variable", w: 700 },
  { id: "modern", label: "Inter", h: "Inter Variable", b: "Inter Variable", w: 800 },
  { id: "elegant", label: "Playfair Display + Inter", h: "Playfair Display Variable", b: "Inter Variable", w: 700 },
  { id: "cald", label: "Fraunces + Nunito", h: "Fraunces Variable", b: "Nunito Variable", w: 700 },
  { id: "tehnic", label: "Space Grotesk + Inter", h: "Space Grotesk Variable", b: "Inter Variable", w: 700 },
  { id: "prietenos", label: "Nunito", h: "Nunito Variable", b: "Nunito Variable", w: 800 },
  { id: "clasic", label: "Lora + Inter", h: "Lora Variable", b: "Inter Variable", w: 700 },
];

// ---------------------------------------------------------------- secțiunile

type FieldDef =
  | { key: string; label: string; kind: "text" | "textarea" | "markdown" | "link" | "image"; hint?: string; max?: number }
  | { key: string; label: string; kind: "select"; options: [string, string][] }
  | { key: string; label: string; kind: "number"; options: number[] };

const TONE: FieldDef = { key: "tone", label: "Fundal", kind: "select", options: [["light", "Deschis"], ["dark", "Închis"], ["brand", "Culoarea brandului"]] };
const FOCUS: FieldDef = { key: "focus", label: "Partea vizibilă a imaginii", kind: "select", options: [["center", "Centru"], ["top", "Sus"], ["bottom", "Jos"]] };
const SIDE: FieldDef = { key: "image_side", label: "Imaginea", kind: "select", options: [["left", "În stânga"], ["right", "În dreapta"]] };
const CTA: FieldDef[] = [
  { key: "button_label", label: "Text buton", kind: "text", max: 40 },
  { key: "button_link", label: "Link buton", kind: "link", hint: "Ex: /search, /c/categorie sau o adresă completă" },
];

const SECTIONS: Record<string, { label: string; description: string; defaults: Record<string, unknown>; fields: FieldDef[] }> = {
  hero: {
    label: "Hero", description: "Mesajul principal, cu imagine sau culoare",
    defaults: { heading: "Titlu", text: "", button_label: "Vezi produsele", button_link: "/search", tone: "light" },
    fields: [{ key: "layout", label: "Compoziție", kind: "select", options: [["centered", "Text pe fundal"], ["split", "Text lângă imagine (fără imagine: cel mai nou produs)"], ["full", "Fotografie pe toată lățimea"], ["compact", "O singură bandă"]] },
      { key: "heading", label: "Titlu", kind: "text", max: 120 }, { key: "text", label: "Text", kind: "textarea", max: 400 }, ...CTA,
      { key: "image", label: "Imagine", kind: "image", hint: "La „Fotografie pe toată lățimea”, textul apare alb peste imagine, cu un strat întunecat pentru contrast." }, FOCUS, TONE],
  },
  categories: {
    label: "Categorii", description: "Grila categoriilor magazinului",
    defaults: { heading: "Categorii", limit: 8 },
    fields: [{ key: "heading", label: "Titlu", kind: "text", max: 80 }, { key: "limit", label: "Câte categorii", kind: "number", options: [3, 4, 6, 8, 12] }],
  },
  products: {
    label: "Rând de produse", description: "Produse noi sau dintr-o categorie",
    defaults: { heading: "Produse noi", source: "new", limit: 8 },
    fields: [{ key: "heading", label: "Titlu", kind: "text", max: 80 },
      { key: "source", label: "Produse", kind: "select", options: [["new", "Cele mai noi"], ["category", "Dintr-o categorie"]] },
      { key: "category", label: "Categoria (adresa ei, ex: plante-de-interior)", kind: "text", max: 120 },
      { key: "limit", label: "Câte produse", kind: "number", options: [4, 8, 12] }],
  },
  banner: {
    label: "Banner", description: "Campanie sau colecție, cu imagine și buton",
    defaults: { heading: "Colecția nouă", text: "", button_label: "Descoperă", button_link: "/search", image_side: "left" },
    fields: [{ key: "heading", label: "Titlu", kind: "text", max: 120 }, { key: "text", label: "Text", kind: "textarea", max: 400 }, ...CTA,
      { key: "image", label: "Imagine", kind: "image" }, SIDE, FOCUS, TONE],
  },
  image_text: {
    label: "Text cu imagine", description: "Povestea magazinului, „despre noi”",
    defaults: { heading: "Despre noi", text: "Spune aici povestea magazinului.", image_side: "left" },
    fields: [{ key: "heading", label: "Titlu", kind: "text", max: 120 }, { key: "text", label: "Text (Markdown)", kind: "markdown", max: 3000 },
      { key: "image", label: "Imagine", kind: "image" }, SIDE],
  },
  trust: {
    label: "Avantaje", description: "Livrare, retur, plată: 1–4 argumente",
    defaults: { items: [{ icon: "truck", title: "Livrare rapidă", text: "Expediem în 1–3 zile lucrătoare." }, { icon: "return", title: "Retur în 14 zile", text: "Conform legii." }] },
    fields: [],
  },
  faq: {
    label: "Întrebări frecvente", description: "Întrebări și răspunsuri scurte",
    defaults: { heading: "Întrebări frecvente", items: [{ q: "Cât durează livrarea?", a: "De obicei 1–3 zile lucrătoare." }] },
    fields: [{ key: "heading", label: "Titlu", kind: "text", max: 80 }],
  },
  rich_text: {
    label: "Text", description: "Un paragraf liber, cu titlu opțional",
    defaults: { heading: "", text: "Textul tău aici." },
    fields: [{ key: "heading", label: "Titlu (opțional)", kind: "text", max: 120 }, { key: "text", label: "Text (Markdown)", kind: "markdown", max: 5000 }],
  },
};

const TRUST_ICONS: [string, string][] = [["truck", "Livrare"], ["return", "Retur"], ["card", "Plată"], ["phone", "Telefon"], ["shield", "Garanție"], ["gift", "Cadou"]];

const newId = () => Math.random().toString(36).slice(2, 10);

// ---------------------------------------------------------------- pagina

export default function Aspect() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const canEdit = me?.user?.role === "owner" || me?.user?.role === "admin";
  const state = useQuery({ queryKey: ["theme"], queryFn: () => admin.get<ThemeState>("/theme") });
  const domains = useQuery({ queryKey: ["domains"], queryFn: () => admin.get<List<Domain>>("/domains") });
  const host = domains.data?.data.find((d) => d.is_primary)?.host ?? domains.data?.data[0]?.host;
  // Local (http): storefront-ul de pe localhost:3100 servește doar magazinul demo; alt magazin se vede pe un
  // domeniu *.localhost adăugat lui (ex: sonora.localhost), dacă are unul.
  const local = domains.data?.data.find((d) => d.host.endsWith(".localhost"))?.host;
  const base = !host ? "" : location.protocol === "https:" ? `https://${host}` : `http://${local ?? "localhost"}:3100`;

  const [tab, setTab] = useState<Tab>("presets");
  const [doc, setDoc] = useState<ThemeDoc | null>(null);
  const [past, setPast] = useState<ThemeDoc[]>([]);
  const [future, setFuture] = useState<ThemeDoc[]>([]);
  const [saving, setSaving] = useState<"idle" | "pending" | "saving" | "error">("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  const [previewPath, setPreviewPath] = useState<string>();
  const [frame, setFrame] = useState({ src: "", n: 0 });
  const frameRef = useRef<HTMLIFrameElement>(null);
  const framePath = useRef("/"); // pagina magazinului deschisă acum în previzualizare (o anunță bara ei)
  const framed = useRef(false); // previzualizarea ciornei din iframe s-a încărcat și ascultă mesaje
  const [device, setDevice] = useState<"mobile" | "desktop">("mobile");
  const [open, setOpen] = useState<string>();
  const lastPush = useRef(0);
  const st = state.data;

  // loadFrame: încarcă în iframe ciorna (path = linkul de previzualizare) sau tema publicată, pe pagina deschisă.
  // Ieșirea explicită din previzualizare șterge sesiunea de ciornă a iframe-ului (după publicare, renunțare).
  const loadFrame = useCallback((path?: string) => {
    framed.current = false;
    setFrame((f) => ({ src: `${base}${path ?? "/theme-preview/iesire"}?la=${encodeURIComponent(framePath.current)}`, n: f.n + 1 }));
  }, [base]);
  // La deschidere: cu o ciornă existentă, previzualizarea pornește direct pe ea (cu un link nou: tokenul nu se
  // poate citi înapoi, doar regenera); altfel se vede tema publicată.
  const hasStoredDraft = !!st?.draft;
  useEffect(() => {
    if (!base || !st) return;
    if (hasStoredDraft && canEdit) {
      admin.post<{ path: string }>("/theme/preview-link").then((r) => { setPreviewPath(r.path); loadFrame(r.path); }, () => loadFrame());
    } else loadFrame();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- doar la prima încărcare a stării
  }, [base, !!st]);

  // Documentul de lucru pornește de la ciornă (dacă există) sau de la tema publicată.
  useEffect(() => {
    if (st && !doc) setDoc(structuredClone((st.draft ?? st.published).doc));
  }, [st, doc]);

  // change: modificare cu istoric pentru anulare (tastarea continuă se grupează într-un singur pas).
  const change = useCallback((next: ThemeDoc) => {
    setDoc((cur) => {
      if (cur && Date.now() - lastPush.current > 800) {
        setPast((p) => [...p.slice(-49), cur]);
        setFuture([]);
      }
      lastPush.current = Date.now();
      return next;
    });
    setSaving("pending");
  }, []);
  const undo = () => { if (!doc || !past.length) return; setFuture((f) => [doc, ...f]); setDoc(past[past.length - 1]); setPast(past.slice(0, -1)); setSaving("pending"); };
  const redo = () => { if (!doc || !future.length) return; setPast((p) => [...p, doc]); setDoc(future[0]); setFuture(future.slice(1)); setSaving("pending"); };

  // Salvarea automată a ciornei, apoi reîncărcarea previzualizării.
  const docRef = useRef(doc);
  docRef.current = doc;
  useEffect(() => {
    if (saving !== "pending" || !canEdit) return;
    const t = setTimeout(async () => {
      setSaving("saving");
      try {
        const s = await admin.put<ThemeState>("/theme/draft", docRef.current);
        qc.setQueryData(["theme"], s);
        setErrors({});
        // Previzualizarea deja deschisă se reîncarcă pe loc, pe pagina pe care e; altfel o pornim acum.
        if (previewPath && framed.current) frameRef.current?.contentWindow?.postMessage({ type: "shopcore:reload" }, new URL(base).origin);
        else {
          const path = previewPath ?? (await admin.post<{ path: string }>("/theme/preview-link")).path;
          setPreviewPath(path);
          loadFrame(path);
        }
        setSaving((cur) => (cur === "saving" ? "idle" : cur));
      } catch (e) {
        setErrors(e instanceof ApiError ? e.problem.errors ?? {} : {});
        setMsg({ ok: false, text: errorMessage(e) });
        setSaving("error");
      }
    }, 700);
    return () => clearTimeout(t);
  }, [saving, doc, canEdit, previewPath, qc, base, loadFrame]);

  // Clic pe o secțiune în previzualizare: o deschide în editor.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      // Doar mesajele din previzualizarea magazinului (aceeași origine ca iframe-ul).
      if (!base || e.origin !== new URL(base).origin) return;
      if (e.data?.type === "shopcore:section" && typeof e.data.id === "string") { setTab("sections"); setOpen(e.data.id); }
      if (e.data?.type === "shopcore:path" && typeof e.data.path === "string") { framePath.current = e.data.path; framed.current = true; }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [base]);

  const publish = useMutation({
    mutationFn: () => admin.post<ThemeState>("/theme/publish"),
    onSuccess: (s) => {
      qc.setQueryData(["theme"], s);
      qc.invalidateQueries({ queryKey: ["theme-versions"] });
      setPreviewPath(undefined);
      setMsg({ ok: true, text: "Tema a fost publicată. Magazinul se actualizează în câteva secunde." });
      // Paginile magazinului se regenerează în ~2 s după publicare: reîncărcăm previzualizarea după aceea.
      setTimeout(() => loadFrame(), 3500);
    },
    onError: (e) => setMsg({ ok: false, text: errorMessage(e) }),
  });
  const discard = useMutation({
    mutationFn: () => admin.del("/theme/draft").then(() => admin.get<ThemeState>("/theme")),
    onSuccess: (s) => { qc.setQueryData(["theme"], s); setDoc(structuredClone(s.published.doc)); setPast([]); setFuture([]); setPreviewPath(undefined); loadFrame(); setSaving("idle"); setMsg({ ok: true, text: "Ai renunțat la modificările nepublicate." }); },
  });
  const share = useMutation({
    mutationFn: async () => {
      const r = await admin.post<{ path: string }>("/theme/preview-link");
      setPreviewPath(r.path);
      loadFrame(r.path);
      await navigator.clipboard?.writeText(base + r.path).catch(() => undefined);
      return base + r.path;
    },
    onSuccess: (url) => setMsg({ ok: true, text: `Link de previzualizare copiat: ${url} (valabil până la publicare; linkurile vechi nu mai merg).` }),
    onError: (e) => setMsg({ ok: false, text: errorMessage(e) }),
  });

  if (state.isLoading || !doc || !st) return <Spinner />;
  const palette = (st.draft ?? st.published).palette;
  const hasDraft = !!st.draft || saving !== "idle";

  return (
    <div className="flex h-[100dvh] min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-2.5">
        <h1 className="mr-2 text-lg font-bold">Aspect</h1>
        <SaveState saving={saving} hasDraft={!!st.draft} publishedAt={st.published_at} />
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant="ghost" disabled={!past.length} onClick={undo} title="Anulează">↶</Button>
          <Button size="sm" variant="ghost" disabled={!future.length} onClick={redo} title="Refă">↷</Button>
          <div className="mx-1 flex rounded-lg border border-slate-200 p-0.5 text-xs">
            {(["mobile", "desktop"] as const).map((d) => (
              <button key={d} onClick={() => setDevice(d)} className={`rounded-md px-2.5 py-1 font-semibold ${device === d ? "bg-slate-900 text-white" : "text-slate-500"}`}>
                {d === "mobile" ? "Mobil" : "Desktop"}
              </button>
            ))}
          </div>
          {canEdit && <>
            <Button size="sm" variant="secondary" disabled={share.isPending} onClick={() => share.mutate()}>Link de previzualizare</Button>
            {st.draft && <Button size="sm" variant="ghost" onClick={() => confirm("Renunți la toate modificările nepublicate?") && discard.mutate()}>Renunță</Button>}
            <Button size="sm" disabled={!hasDraft || saving === "pending" || saving === "saving" || saving === "error" || publish.isPending}
              onClick={() => publish.mutate()}>Publică</Button>
          </>}
        </div>
      </div>
      {msg && <div className="px-4 pt-3"><Alert tone={msg.ok ? "green" : "red"}>{msg.text}</Alert></div>}
      {Object.keys(errors).length > 0 && (
        <div className="px-4 pt-2 text-xs text-red-600">{Object.entries(errors).slice(0, 4).map(([k, v]) => <div key={k}>{k}: {v}</div>)}</div>
      )}
      <div className="flex min-h-0 flex-1">
        <aside className="flex w-[22rem] shrink-0 flex-col border-r border-slate-200 bg-white">
          <nav className="flex border-b border-slate-200 text-xs font-semibold">
            {TABS.map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)} className={`flex-1 px-2 py-2.5 ${tab === t ? "border-b-2 border-brand text-brand" : "text-slate-500 hover:text-slate-800"}`}>{label}</button>
            ))}
          </nav>
          {/* min-w-0: un <fieldset> are implicit min-width: min-content și s-ar lăți după cel mai lung titlu. */}
          <fieldset disabled={!canEdit} className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4">
            {tab === "presets" && <Presets doc={doc} canEdit={canEdit} onApply={change} />}
            {tab === "style" && <StylePanel doc={doc} palette={palette} onChange={change} />}
            {tab === "sections" && <SectionsPanel doc={doc} onChange={change} open={open} setOpen={setOpen} errors={errors} />}
            {tab === "nav" && <NavPanel doc={doc} onChange={change} />}
            {tab === "history" && <History onRestored={(s) => { qc.setQueryData(["theme"], s); setDoc(structuredClone(s.draft!.doc)); setSaving("pending"); }} />}
          </fieldset>
        </aside>
        <div className="flex min-w-0 flex-1 items-start justify-center overflow-auto bg-slate-100 p-4">
          {frame.src ? (
            <iframe key={frame.n} ref={frameRef} src={frame.src} title="Previzualizarea magazinului"
              className={`h-full rounded-lg border border-slate-300 bg-white shadow-sm ${device === "mobile" ? "w-[390px]" : "w-full"}`} />
          ) : <p className="text-sm text-slate-500">Previzualizarea apare după ce magazinul are un domeniu.</p>}
        </div>
      </div>
    </div>
  );
}

function SaveState({ saving, hasDraft, publishedAt }: { saving: string; hasDraft: boolean; publishedAt?: string }) {
  if (saving === "pending" || saving === "saving") return <Badge>se salvează ciorna…</Badge>;
  if (saving === "error") return <Badge tone="red">ciorna nu s-a salvat</Badge>;
  if (hasDraft) return <Badge tone="amber">modificări nepublicate</Badge>;
  return <Badge tone="green">{publishedAt ? `publicată ${date(publishedAt)}` : "tema implicită"}</Badge>;
}

// ---------------------------------------------------------------- teme de pornire

// applyPreset: tema nouă ia doar stilul temei de pornire. Secțiunile completate (texte, imagini, linkuri) rămân;
// eroul preia doar așezarea și tonul temei, care țin de aspect. Un magazin fără nicio secțiune primește
// secțiunile temei, ca punct de plecare.
function applyPreset(cur: ThemeDoc, preset: ThemeDoc): ThemeDoc {
  const next = structuredClone(cur);
  next.preset = preset.preset;
  next.style = { ...preset.style, logo: cur.style.logo };
  if (next.home.sections.length === 0) {
    next.home.sections = structuredClone(preset.home.sections);
    return next;
  }
  const hero = preset.home.sections.find((s) => s.type === "hero")?.settings;
  if (hero) {
    for (const s of next.home.sections) {
      if (s.type === "hero") s.settings = { ...s.settings, layout: hero.layout, tone: hero.tone };
    }
  }
  return next;
}

function Presets({ doc, canEdit, onApply }: { doc: ThemeDoc; canEdit: boolean; onApply: (d: ThemeDoc) => void }) {
  const q = useQuery({ queryKey: ["theme-presets"], queryFn: () => admin.get<List<ThemePreset>>("/theme/presets") });
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Tema schimbă doar aspectul: culorile, fonturile, colțurile, cardurile și așezarea eroului. Secțiunile
        paginii principale și textele lor, logo-ul, meniurile și bara de anunț rămân cum le-ai lăsat.
        Alege o temă și o vezi imediat în previzualizare; cu ↶ revii la cea de dinainte. Totul intră în ciornă: nimic
        nu se vede în magazin până la „Publică”.
      </p>
      {q.data?.data.map((p) => {
        const active = doc.preset === p.id;
        return (
          // Clic pe card: tema intră imediat în ciornă și în previzualizare; „Anulează” (↶) o readuce pe cea de dinainte.
          <button key={p.id} type="button" aria-pressed={active} disabled={!canEdit || active}
            onClick={() => onApply(applyPreset(doc, p.doc))}
            className={`block w-full rounded-lg border p-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-default ${
              active ? "border-brand bg-brand-soft" : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"}`}>
            <span className="flex items-center gap-2">
              <span aria-hidden className="h-5 w-5 shrink-0 rounded-full border border-black/10" style={{ background: p.doc.style.brand }} />
              <b className="min-w-0 truncate text-base" style={{ fontFamily: `"${FONTS.find((f) => f.id === p.doc.style.fonts)?.h ?? "inherit"}"` }}>{p.name}</b>
              {active && <Badge tone="blue">activă</Badge>}
            </span>
            <span className="mt-1 block text-xs text-slate-500">{p.description}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- stil

function StylePanel({ doc, palette, onChange }: { doc: ThemeDoc; palette: { accent: string; accent_ink: string; accent_text: string; accent_soft: string; adjusted: boolean }; onChange: (d: ThemeDoc) => void }) {
  const set = (patch: Partial<ThemeDoc["style"]>) => onChange({ ...doc, style: { ...doc.style, ...patch } });
  const valid = /^#[0-9a-fA-F]{6}$/.test(doc.style.brand);
  return (
    <div className="space-y-5">
      <ImageField label="Logo" hint="PNG transparent sau SVG convertit în PNG; apare în antet în locul numelui." value={doc.style.logo ?? ""} onChange={(logo) => set({ logo: logo || undefined })} />
      <Field label="Culoarea brandului" hint="Restul paletei se calculează automat, cu contrast suficient pentru citire (WCAG).">
        <div className="flex gap-2">
          <input type="color" className="h-9 w-12 cursor-pointer rounded border border-slate-200" value={valid ? doc.style.brand : "#000000"} onChange={(e) => set({ brand: e.target.value })} />
          <input className="input font-mono" value={doc.style.brand} onChange={(e) => set({ brand: e.target.value.trim() })} />
        </div>
      </Field>
      <div>
        <div className="mb-1 text-xs font-semibold text-slate-600">Paleta rezultată</div>
        <div className="flex gap-2">
          <span className="rounded-md px-3 py-1.5 text-xs font-semibold" style={{ background: palette.accent, color: palette.accent_ink }}>Buton</span>
          <span className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold" style={{ color: palette.accent_text }}>Link</span>
          <span className="rounded-md px-3 py-1.5 text-xs" style={{ background: palette.accent_soft }}>Fundal</span>
        </div>
        {palette.adjusted && <p className="mt-1.5 text-xs text-amber-700">Am închis puțin culoarea pentru butoane, ca textul să fie lizibil.</p>}
      </div>
      <div>
        <div className="mb-1 text-xs font-semibold text-slate-600">Fonturi</div>
        <div className="space-y-1.5">
          {FONTS.map((f) => (
            <button key={f.id} type="button" onClick={() => set({ fonts: f.id })}
              className={`block w-full rounded-lg border px-3 py-2 text-left ${doc.style.fonts === f.id ? "border-brand bg-brand-soft" : "border-slate-200 hover:border-slate-300"}`}>
              <span className="block text-lg leading-tight" style={{ fontFamily: `"${f.h}"`, fontWeight: f.w }}>Gustul de acasă</span>
              <span className="block text-xs text-slate-500" style={{ fontFamily: `"${f.b}"` }}>{f.label} · livrare în 1–3 zile</span>
            </button>
          ))}
        </div>
      </div>
      <Choice label="Colțuri" value={doc.style.radius} options={[["none", "Drepte"], ["soft", "Ușor rotunjite"], ["round", "Rotunjite"]]} onChange={(radius) => set({ radius: radius as ThemeDoc["style"]["radius"] })} />
      <Choice label="Butoane" value={doc.style.buttons} options={[["solid", "Ca restul colțurilor"], ["pill", "Rotunde (pastilă)"]]} onChange={(buttons) => set({ buttons: buttons as ThemeDoc["style"]["buttons"] })} />
      <div className="border-t border-slate-100 pt-4 text-sm font-bold">Compoziție</div>
      <Choice label="Cardurile de produs" value={doc.style.cards ?? "standard"}
        options={[["standard", "Pătrate"], ["tall", "Înalte, fără chenar"], ["dense", "Compacte, cu stoc"]]}
        onChange={(cards) => set({ cards: cards as ThemeDoc["style"]["cards"] })} />
      <Choice label="Densitate" value={doc.style.density ?? "airy"} options={[["airy", "Aerisit"], ["compact", "Compact (mai multe produse pe rând)"]]}
        onChange={(density) => set({ density: density as ThemeDoc["style"]["density"] })} />
      <Choice label="Antet" value={doc.style.header ?? "left"} options={[["left", "Logo în stânga"], ["center", "Logo centrat"]]}
        onChange={(header) => set({ header: header as ThemeDoc["style"]["header"] })} />
      <Choice label="Fundal" value={doc.style.paper ?? "white"} options={[["white", "Alb"], ["tinted", "Ușor nuanțat în culoarea brandului"]]}
        onChange={(paper) => set({ paper: paper as ThemeDoc["style"]["paper"] })} />
      <Choice label="Griuri (text secundar, linii)" value={doc.style.neutrals ?? "neutral"} options={[["neutral", "Neutre"], ["brand", "În nuanța brandului"]]}
        onChange={(neutrals) => set({ neutrals: neutrals as ThemeDoc["style"]["neutrals"] })} />
      <Choice label="Ornament" value={doc.style.ornament ?? "none"} options={[["none", "Fără"], ["cusatura", "Motiv de cusătură"]]}
        onChange={(ornament) => set({ ornament: ornament as ThemeDoc["style"]["ornament"] })} />
    </div>
  );
}

function Choice({ label, value, options, onChange }: { label: string; value: string; options: [string, string][]; onChange: (v: string) => void }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold text-slate-600">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map(([v, l]) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${value === v ? "border-brand bg-brand-soft text-brand" : "border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- secțiuni

function SectionsPanel({ doc, onChange, open, setOpen, errors }: {
  doc: ThemeDoc; onChange: (d: ThemeDoc) => void; open?: string; setOpen: (id?: string) => void; errors: Record<string, string>;
}) {
  const [adding, setAdding] = useState(false);
  const list = doc.home.sections;
  const setList = (sections: ThemeSection[]) => onChange({ ...doc, home: { sections } });
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    setList(next);
  };
  const add = (type: string) => {
    const s: ThemeSection = { id: newId(), type, settings: structuredClone(SECTIONS[type].defaults) };
    setList([...list, s]);
    setOpen(s.id);
    setAdding(false);
  };
  return (
    <div className="space-y-2">
      <p className="mb-2 text-xs text-slate-500">Secțiunile paginii principale, în ordine. Clic pe o secțiune în previzualizare o deschide aici.</p>
      {list.map((s, i) => {
        const meta = SECTIONS[s.type];
        const hasErr = Object.keys(errors).some((k) => k.startsWith(`home.sections[${i}]`));
        return (
          <div key={s.id} className={`rounded-lg border ${open === s.id ? "border-brand" : hasErr ? "border-red-300" : "border-slate-200"} ${s.hidden ? "opacity-60" : ""}`}>
            <div className="flex items-center gap-1 px-2 py-1.5">
              <button type="button" className="min-w-0 flex-1 truncate text-left text-sm font-semibold" onClick={() => setOpen(open === s.id ? undefined : s.id)}>
                {meta?.label ?? s.type}
                <span className="ml-1.5 font-normal text-slate-400">{String(s.settings.heading ?? "")}</span>
              </button>
              <IconBtn title="Mută în sus" onClick={() => move(i, -1)}>↑</IconBtn>
              <IconBtn title="Mută în jos" onClick={() => move(i, 1)}>↓</IconBtn>
              <IconBtn title={s.hidden ? "Arată" : "Ascunde"} onClick={() => setList(list.map((x) => (x.id === s.id ? { ...x, hidden: !x.hidden } : x)))}>{s.hidden ? "◌" : "●"}</IconBtn>
              <IconBtn title="Șterge" onClick={() => confirm(`Ștergi secțiunea „${meta?.label ?? s.type}”?`) && setList(list.filter((x) => x.id !== s.id))}>✕</IconBtn>
            </div>
            {open === s.id && meta && (
              <div className="space-y-3 border-t border-slate-100 p-3">
                <SectionForm s={s} onChange={(settings) => setList(list.map((x) => (x.id === s.id ? { ...x, settings } : x)))} />
              </div>
            )}
          </div>
        );
      })}
      {list.length < 25 && (adding ? (
        <div className="space-y-1.5 rounded-lg border border-dashed border-slate-300 p-2">
          {Object.entries(SECTIONS).map(([type, m]) => (
            <button key={type} type="button" onClick={() => add(type)} className="block w-full rounded-md px-2 py-1.5 text-left hover:bg-slate-50">
              <div className="text-sm font-semibold">{m.label}</div>
              <div className="text-xs text-slate-500">{m.description}</div>
            </button>
          ))}
          <Button size="sm" variant="ghost" type="button" onClick={() => setAdding(false)}>Anulează</Button>
        </div>
      ) : <Button size="sm" variant="secondary" type="button" className="w-full" onClick={() => setAdding(true)}>+ Adaugă secțiune</Button>)}
    </div>
  );
}

function IconBtn({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" title={title} aria-label={title} onClick={onClick} className="shrink-0 rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-900">{children}</button>;
}

function SectionForm({ s, onChange }: { s: ThemeSection; onChange: (settings: Record<string, unknown>) => void }) {
  const meta = SECTIONS[s.type];
  const v = s.settings;
  const set = (key: string, val: unknown) => onChange({ ...v, [key]: val === "" ? undefined : val });
  return (
    <>
      {meta.fields.map((f) => {
        if (f.key === "category" && v.source !== "category") return null;
        const val = v[f.key];
        switch (f.kind) {
          case "select":
            return (
              <Field key={f.key} label={f.label}>
                <select className="input" value={String(val ?? f.options[0][0])} onChange={(e) => set(f.key, e.target.value)}>
                  {f.options.map(([o, l]) => <option key={o} value={o}>{l}</option>)}
                </select>
              </Field>
            );
          case "number":
            return (
              <Field key={f.key} label={f.label}>
                <select className="input" value={String(val ?? f.options[0])} onChange={(e) => set(f.key, Number(e.target.value))}>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
            );
          case "image":
            return <ImageField key={f.key} label={f.label} hint={f.hint} value={String(val ?? "")} onChange={(url) => set(f.key, url)} />;
          case "textarea":
          case "markdown":
            return (
              <Field key={f.key} label={f.label} hint={f.kind === "markdown" ? "**îngroșat**, - listă, [link](/pagina/...)" : undefined}>
                <textarea className="input min-h-24 text-sm" maxLength={f.max} value={String(val ?? "")} onChange={(e) => set(f.key, e.target.value)} />
              </Field>
            );
          default:
            return (
              <Field key={f.key} label={f.label} hint={"hint" in f ? f.hint : undefined}>
                <input className="input" maxLength={"max" in f ? f.max : undefined} value={String(val ?? "")} onChange={(e) => set(f.key, e.target.value)} />
              </Field>
            );
        }
      })}
      {s.type === "trust" && <TrustItems items={(v.items as Record<string, string>[]) ?? []} onChange={(items) => set("items", items)} />}
      {s.type === "faq" && <FaqItems items={(v.items as Record<string, string>[]) ?? []} onChange={(items) => set("items", items)} />}
    </>
  );
}

function TrustItems({ items, onChange }: { items: Record<string, string>[]; onChange: (i: Record<string, string>[]) => void }) {
  const upd = (i: number, k: string, val: string) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: val } : it)));
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="space-y-1.5 rounded-md bg-slate-50 p-2">
          <div className="flex gap-1.5">
            <select className="input w-28 shrink-0" value={it.icon} onChange={(e) => upd(i, "icon", e.target.value)}>
              {TRUST_ICONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input className="input min-w-0" placeholder="Titlu" maxLength={60} value={it.title ?? ""} onChange={(e) => upd(i, "title", e.target.value)} />
            <IconBtn title="Șterge" onClick={() => onChange(items.filter((_, j) => j !== i))}>✕</IconBtn>
          </div>
          <input className="input" placeholder="Text scurt" maxLength={160} value={it.text ?? ""} onChange={(e) => upd(i, "text", e.target.value)} />
        </div>
      ))}
      {items.length < 4 && <Button size="sm" variant="ghost" type="button" onClick={() => onChange([...items, { icon: "shield", title: "Avantaj", text: "" }])}>+ Avantaj</Button>}
    </div>
  );
}

function FaqItems({ items, onChange }: { items: Record<string, string>[]; onChange: (i: Record<string, string>[]) => void }) {
  const upd = (i: number, k: string, val: string) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: val } : it)));
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="space-y-1.5 rounded-md bg-slate-50 p-2">
          <div className="flex gap-1.5">
            <input className="input min-w-0" placeholder="Întrebare" maxLength={200} value={it.q ?? ""} onChange={(e) => upd(i, "q", e.target.value)} />
            <IconBtn title="Șterge" onClick={() => onChange(items.filter((_, j) => j !== i))}>✕</IconBtn>
          </div>
          <textarea className="input min-h-16 text-sm" placeholder="Răspuns" maxLength={1500} value={it.a ?? ""} onChange={(e) => upd(i, "a", e.target.value)} />
        </div>
      ))}
      {items.length < 12 && <Button size="sm" variant="ghost" type="button" onClick={() => onChange([...items, { q: "Întrebare nouă", a: "Răspuns." }])}>+ Întrebare</Button>}
    </div>
  );
}

// ImageField: încărcare în biblioteca magazinului (aceleași variante WebP ca pozele de produs) sau URL extern.
function ImageField({ label, hint, value, onChange }: { label: string; hint?: string; value: string; onChange: (url: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  const upload = async (f: File) => {
    setBusy(true);
    setErr(undefined);
    try {
      const form = new FormData();
      form.append("file", f);
      onChange((await admin.upload<Upload>("/media", form)).url);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field label={label} hint={hint} error={err}>
      <div className="flex items-center gap-2">
        {value ? <img src={thumb(value)} alt="" className="h-12 w-16 rounded border border-slate-200 object-cover" /> : <div className="h-12 w-16 rounded border border-dashed border-slate-300" />}
        <label className="cursor-pointer rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold hover:bg-slate-50">
          {busy ? "Se încarcă…" : value ? "Înlocuiește" : "Încarcă"}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={busy} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
        {value && <button type="button" className="text-xs text-slate-500 hover:text-red-600" onClick={() => onChange("")}>Elimină</button>}
      </div>
    </Field>
  );
}

// ---------------------------------------------------------------- navigare

function NavPanel({ doc, onChange }: { doc: ThemeDoc; onChange: (d: ThemeDoc) => void }) {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="text-sm font-bold">Bara de anunț</div>
        <Field label="Text" hint="Gol = bara nu apare. Ex: Livrare gratuită peste 200 lei">
          <input className="input" maxLength={140} value={doc.announcement.text ?? ""} onChange={(e) => onChange({ ...doc, announcement: { ...doc.announcement, text: e.target.value } })} />
        </Field>
        <Field label="Link (opțional)">
          <input className="input" placeholder="/search" value={doc.announcement.link ?? ""} onChange={(e) => onChange({ ...doc, announcement: { ...doc.announcement, link: e.target.value || undefined } })} />
        </Field>
      </div>
      <Links title="Meniul principal" hint="Gol = categoriile magazinului, în ordinea din Categorii." links={doc.nav.main} onChange={(main) => onChange({ ...doc, nav: { ...doc.nav, main } })} />
      <Links title="Linkuri în subsol" hint="Apar în coloana Informații, înaintea paginilor legale." links={doc.nav.footer} onChange={(footer) => onChange({ ...doc, nav: { ...doc.nav, footer } })} />
    </div>
  );
}

function Links({ title, hint, links, onChange }: { title: string; hint: string; links: ThemeLink[]; onChange: (l: ThemeLink[]) => void }) {
  const upd = (i: number, k: keyof ThemeLink, v: string) => onChange(links.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= links.length) return;
    const n = [...links];
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n);
  };
  return (
    <div className="space-y-2">
      <div className="text-sm font-bold">{title}</div>
      <p className="text-xs text-slate-500">{hint}</p>
      {links.map((l, i) => (
        <div key={i} className="flex gap-1">
          <input className="input w-28 min-w-0 shrink-0" placeholder="Text" maxLength={40} value={l.label} onChange={(e) => upd(i, "label", e.target.value)} />
          <input className="input min-w-0" placeholder="/c/categorie" value={l.url} onChange={(e) => upd(i, "url", e.target.value)} />
          <IconBtn title="Mută în sus" onClick={() => move(i, -1)}>↑</IconBtn>
          <IconBtn title="Șterge" onClick={() => onChange(links.filter((_, j) => j !== i))}>✕</IconBtn>
        </div>
      ))}
      {links.length < 12 && <Button size="sm" variant="ghost" type="button" onClick={() => onChange([...links, { label: "Link", url: "/" }])}>+ Link</Button>}
    </div>
  );
}

// ---------------------------------------------------------------- istoric

function History({ onRestored }: { onRestored: (s: ThemeState) => void }) {
  const q = useQuery({ queryKey: ["theme-versions"], queryFn: () => admin.get<List<ThemeVersion>>("/theme/versions") });
  const restore = useMutation({ mutationFn: (id: string) => admin.post<ThemeState>(`/theme/versions/${id}/restore`), onSuccess: onRestored });
  if (q.isLoading) return <Spinner />;
  if (!q.data?.data.length) return <p className="text-sm text-slate-500">Încă nu ai publicat nicio versiune: magazinul folosește tema implicită.</p>;
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">Versiunile publicate. „Restaurează” o aduce în ciornă; o publici când ești mulțumit.</p>
      {q.data.data.map((v) => (
        <div key={v.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <div>
            <div className="font-semibold">{date(v.published_at)} {v.current && <Badge tone="green">în magazin</Badge>}</div>
            <div className="text-xs text-slate-500">{v.preset || "temă personalizată"}{v.published_by && ` · ${v.published_by}`}</div>
          </div>
          {!v.current && <Button size="sm" variant="ghost" disabled={restore.isPending} onClick={() => restore.mutate(v.id)}>Restaurează</Button>}
        </div>
      ))}
    </div>
  );
}
